import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useT } from "@/lib/i18n";

const DISMISS_KEY = "slowcafe-sticky-cta-dismissed-v1";

/**
 * Sticky bottom CTA bar — drives direct-to-checkout conversion. On mobile it
 * appears as a full-bleed dark bar at the bottom; on desktop it morphs into
 * a centered floating pill (max-w-2xl) so it stays a persistent CTA without
 * dominating wide screens.
 *
 * Behaviour rules (all chosen to avoid being annoying):
 *  - Hidden until the user scrolls past 70% of the first viewport — gives
 *    the hero room to breathe before the sales nag appears.
 *  - Disappears whenever the Pricing section itself is on-screen (the bar
 *    would compete with the actual price cards underneath it).
 *  - User can dismiss with the X; choice persists in localStorage so a
 *    closed bar stays closed across page reloads.
 *  - Mobile: bottom padding clears the WhatsApp FAB; iOS safe-area honoured.
 */
export default function StickyCTA() {
    const { t, lang } = useT();
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        try {
            return typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1";
        } catch {
            return false;
        }
    });

    useEffect(() => {
        if (dismissed) return;
        const onScroll = () => {
            // Show after scrolling past 70% of viewport — gives the hero
            // unobstructed first impression.
            const triggerY = window.innerHeight * 0.7;
            const past = window.scrollY > triggerY;

            // Hide when Pricing section is on-screen so the bar isn't
            // duplicate-CTA when the actual cards are right there.
            const pricingEl = document.getElementById("pricing");
            let pricingOnScreen = false;
            if (pricingEl) {
                const rect = pricingEl.getBoundingClientRect();
                pricingOnScreen = rect.top < window.innerHeight - 80 && rect.bottom > 80;
            }
            setVisible(past && !pricingOnScreen);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [dismissed]);

    const dismiss = () => {
        setDismissed(true);
        try {
            localStorage.setItem(DISMISS_KEY, "1");
        } catch {
            // localStorage unavailable (private mode) — dismissal is in-memory only
        }
    };

    if (dismissed) return null;

    const buyLabel = t("sticky.buy_now") || (lang === "cn" ? "IG 内容 AI" : "IG Content AI");
    const tagline = t("sticky.tagline") || (lang === "cn" ? "30 天发布内容 · RM 88 起" : "30 days of posts · from RM 88");

    return (
        <div
            data-testid="sticky-cta"
            className={`fixed inset-x-0 z-40 transition-all duration-300 ${
                visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-full pointer-events-none"
            }`}
            // Sits ABOVE the WhatsApp FAB (bottom-6) but with a small gap on
            // the right so the FAB stays tappable. bottom: 0 + bg blur covers
            // safe-area-inset on iOS notches.
            style={{ bottom: 0 }}
        >
            {/* Mobile: full-bleed dark bar. Desktop: centered floating pill
                with max-width so it doesn't dominate wide screens but still
                stays a persistent CTA. */}
            <div className="bg-cafe-cream/92 backdrop-blur-md border-t border-cafe-espresso/10 lg:bg-transparent lg:border-0 lg:backdrop-blur-none px-3 lg:px-6 pb-[max(0.6rem,env(safe-area-inset-bottom))] lg:pb-6 pt-2.5 lg:pt-0 pr-[5.5rem] lg:pr-6">
                <div className="flex items-center gap-2.5 lg:max-w-2xl lg:mx-auto lg:bg-cafe-cream/95 lg:backdrop-blur-md lg:rounded-full lg:border lg:border-cafe-espresso/15 lg:shadow-[0_10px_40px_-10px_rgba(44,30,22,0.18)] lg:p-2 lg:pl-2">
                    <Link
                        to={lang === "cn" ? "/ig/content/AI/cn" : "/ig/content/AI"}
                        data-testid="sticky-cta-buy"
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-cafe-espresso active:scale-[0.97] hover:bg-cafe-olive transition-all text-cafe-cream px-4 lg:px-6 h-12 lg:h-11"
                    >
                        <Sparkles className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                        <div className="text-left leading-tight">
                            <div className="text-[13px] font-medium">{buyLabel}</div>
                            <div className="text-[10px] opacity-75 font-normal lg:hidden">{tagline}</div>
                        </div>
                        <span className="hidden lg:inline text-[11px] opacity-75 font-normal ml-1">— {tagline}</span>
                    </Link>
                    <button
                        onClick={dismiss}
                        aria-label="Dismiss"
                        data-testid="sticky-cta-dismiss"
                        className="shrink-0 h-9 w-9 rounded-full bg-cafe-espresso/8 text-cafe-espresso/70 flex items-center justify-center hover:bg-cafe-espresso/15"
                    >
                        <X className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                </div>
            </div>
        </div>
    );
}
